import { ApiService } from "Services/ApiService";
import { ResultsDTO } from "Types";
import { SubClassAdminRequestDTO, SubClassMstDTO, UpdatedSubClass } from "Types/Admin/AdminSubClassType";

export interface GetAdminSubClassesParams {
  page?: number;
  size?: number;
  search?: string;
}

interface AdminSubClassesResults {
  subClasses: SubClassMstDTO[];
  totalSubClasses: number;
}

export interface AdminSubClassesListResult {
  subClasses: SubClassMstDTO[];
  totalSubClasses: number;
}

const mapSubClass = (item: SubClassMstDTO) => ({
  id: item.id,
  plant: (item as any).plant || "",
  subClassCode: item.subClassCode,
  subClassName: item.subClassName,
  categoryCode: item.categoryCode,
  categoryName: item.categoryName || "",
  isActive:
    ((item as any).isActive ?? (item as any).active) === true ? "Y" : "N",
});

export const subClassApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAdminSubClasses: builder.query<
      ResultsDTO<SubClassMstDTO[]>,
      GetAdminSubClassesParams | void
    >({
      query: (params) => ({
        url: "admin/sub-class/",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          size: params?.size ?? 1500,
          ...(params?.search ? { search: params.search } : {}),
        },
      }),
      transformResponse: (response: ResultsDTO<AdminSubClassesResults>) => {
        return {
          ...response,
          results: (response.results?.subClasses || []).map(mapSubClass),
        };
      },
      providesTags: [{ type: "SubClass", id: "LIST" }],
    }),

    // Server-side paginated + searchable listing, used by the Admin Sub Category listing page.
    getAdminSubClassesList: builder.query<
      AdminSubClassesListResult,
      { page: number; size: number; search?: string }
    >({
      query: ({ page, size, search }) => ({
        url: "admin/sub-class/",
        method: "GET",
        params: {
          page,
          size,
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (response: ResultsDTO<AdminSubClassesResults>) => ({
        subClasses: (response.results?.subClasses || []).map(mapSubClass),
        totalSubClasses: response.results?.totalSubClasses ?? 0,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.subClasses.map(({ subClassCode }) => ({
                type: "SubClass" as const,
                id: subClassCode,
              })),
              { type: "SubClass", id: "LIST" },
            ]
          : [{ type: "SubClass", id: "LIST" }],
    }),

    getSubClassById: builder.query<ResultsDTO<SubClassMstDTO>, string>({
      query: (id) => ({
        url: `admin/sub-class/${id}`,
        method: "GET",
      }),
      transformResponse: (response: ResultsDTO<SubClassMstDTO>) => {
        return {
          ...response,
          results: {
            ...response.results,
            plant: response.results.plant || "",
            isActive: (response.results as any).active === true ? "Y" : "N",
          },
        };
      },
      providesTags: (result, error, id) => [{ type: "SubClass", id }],
    }),

    createSubClass: builder.mutation<ResultsDTO, SubClassAdminRequestDTO>({
      query: (data) => ({
        url: "admin/sub-class/create",
        method: "POST",
        body: {
          ...data,
          isActive: data.isActive || "Y",
        },
      }),
      invalidatesTags: [{ type: "SubClass", id: "LIST" }],
    }),

    updateSubClass: builder.mutation<
      ResultsDTO,
      { id: string; data: Partial<UpdatedSubClass> }
    >({
      query: ({ id, data }) => ({
        url: `admin/sub-class/update/${id}`,
        method: "PUT",
        body: {
          ...data,
          ...(data.isActive !== undefined && {
            isActive:
              typeof data.isActive === "boolean"
                ? data.isActive
                  ? "Y"
                  : "N"
                : data.isActive,
          }),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SubClass", id },
        { type: "SubClass", id: "LIST" },
      ],
    }),

    deleteSubClass: builder.mutation<ResultsDTO, string>({
      query: (id) => ({
        url: `admin/sub-class/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "SubClass" }],
    }),

    bulkDeleteSubClasses: builder.mutation<ResultsDTO, { ids: number[] }>({
      query: ({ ids }) => ({
        url: `/admin/sub-class/bulk-delete`,
        method: "DELETE",
        params: { ids },
      }),
      invalidatesTags: [{ type: "SubClass", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAdminSubClassesQuery,
  useGetAdminSubClassesListQuery,
  useGetSubClassByIdQuery,
  useCreateSubClassMutation,
  useUpdateSubClassMutation,
  useDeleteSubClassMutation,
  useBulkDeleteSubClassesMutation,
} = subClassApiSlice;
